/*
  Загрузка перечня банкротов файлом - §3.7.

  Зачем это здесь. Пошлина, уплаченная за банкрота, уходит впустую, а долг
  списывается: по отраслевым данным таких должников в портфеле до пятнадцати
  процентов (§3.7.1). Внести процедуру можно и на карточке счёта руками, но по
  одному должнику, а перечень приходит списком - из реестра сведений о
  банкротстве либо из таблицы, которую ведёт юрист.

  🔴 Арбитр ЖКХ не узнаёт о банкротстве сам: доступ к реестру сведений о
  банкротстве не подключён, и до тех пор это единственный способ внести сотни
  строк. Так и сказано на экране - молчание об этом читалось бы как «система
  проверяет».

  Два обращения на загрузку: сперва «по кому остановится взыскание», потом
  запись по подтверждению.

  🔴 С 20.10.2026 файл уходит на сервер ОДИН раз и разбирается один раз
  (решение владельца 19.10.2026, `useUploadFlow`): «Посмотреть» и запись
  называют уже прочитанный файл номером, запись идёт фоновой работой с ходом,
  конец - блок «Готово»; перечень процедур остаётся под ним.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { rows as rowsCount } from '../api/format.js'
import { Notice } from './Common.jsx'
import UploadEnd from './upload/UploadEnd.jsx'
import UploadStages from './upload/UploadStages.jsx'
import useUploadFlow from './upload/useUploadFlow.js'
import UploadHelp from './UploadHelp.jsx'

/*
  Образец файла - он же картинка над кнопкой «Скачать образец». Вторая строка
  показывает завершённую процедуру: у неё обязательна дата завершения, и без
  примера человек узнаёт об этом только из отказа.
*/
const SAMPLE = {
  file: 'банкроты-образец.csv',
  headers: [
    'Лицевой счёт',
    'ФИО должника',
    'Вид процедуры',
    'Состояние',
    'Дата возбуждения',
    'Номер дела',
    'Закрытие реестра требований',
    'Дата завершения',
  ],
  rows: [
    ['20346', 'Иванов Иван Иванович', 'судебное', 'введена', '16.03.2026', 'А40-12345/2026', '01.06.2026', ''],
    ['11852', 'Петров Пётр Петрович', 'внесудебное', 'завершена', '10.01.2025', '', '', '15.07.2025'],
  ],
}

const SHOWN_ROWS = 20
/* Сколько строк показать. Полное число стоит рядом: список в тысячу строк не
   читается, а двадцати хватает, чтобы понять, что не так с файлом. */

function Plan({ plan }) {
  return (
    <div className="onec__summary">
      <p>
        Строк в файле: <b>{rowsCount(plan.total)}</b>. Будет внесено: <b>{plan.written}</b>.
      </p>
      {plan.changes.slice(0, SHOWN_ROWS).map((change) => (
        <p key={`${change.person_id}-${change.account_number}`}>
          Счёт {change.account_number}, {change.full_name}
          {change.case_number ? `, дело ${change.case_number}` : ''}: подача остановится.
        </p>
      ))}
      {plan.issues.length > 0 ? (
        <>
          <p className="onec__skipped">
            Требуют внимания: {rowsCount(plan.issues.length)}. {plan.issues.length === 1 ? 'Она не записана.' : 'Они не записаны.'}
          </p>
          {plan.issues.slice(0, SHOWN_ROWS).map((issue) => (
            <p key={`${issue.row_number}-${issue.account_number ?? ''}`} className="onec__skipped">
              Строка {issue.row_number}: {issue.explanation}
            </p>
          ))}
        </>
      ) : null}
    </div>
  )
}

export default function BankruptcyUpload({ onLoaded }) {
  const flow = useUploadFlow('bankruptcy')
  const file = flow.stored
  const [plan, setPlan] = useState(null)
  const [written, setWritten] = useState(false)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  // Номер захода: новый заход пересоздаёт поле выбора без имени прежнего файла.
  const [round, setRound] = useState(0)

  function choose(chosen) {
    setPlan(null)
    setWritten(false)
    setError(null)
    flow.choose(chosen ?? null)
  }

  function again() {
    choose(null)
    setRound(round + 1)
  }

  async function act(action, then) {
    setBusy(true)
    setError(null)
    try {
      then(await action())
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const look = () =>
    act(
      () =>
        flow.check(
          () => api.previewBankruptcies(file),
          (seen) => `строк в файле: ${seen.total}, будет внесено: ${seen.written}`,
        ),
      setPlan,
    )

  // Запись - фоновой работой по прочитанному файлу (20.10.2026); ответ тот же,
  // что отдавал запрос записи: сервер вызывает тот же обработчик.
  const load = () =>
    act(
      () => flow.write('bankruptcy', { column_overrides: null }),
      (done) => {
        setPlan(done)
        setWritten(true)
        onLoaded()
      },
    )

  return (
    <>
      <UploadHelp
        title="Где это взять"
        where={[
          'В Едином федеральном реестре сведений о банкротстве (fedresurs.ru) найдите ' +
            'публикации по своим должникам и выпишите: ФИО, вид процедуры, дату возбуждения ' +
            'дела, номер дела и дату закрытия реестра требований кредиторов.',
          'Лицевой счёт подставьте свой - по нему Арбитр ЖКХ находит, к кому из жильцов ' +
            'относится строка: на одном счёте бывает несколько должников.',
        ]}
        sample={SAMPLE}
        note={
          'Вид процедуры и состояние обязательны и не угадываются: судебное банкротство и ' +
          'внесудебное через МФЦ ведутся по-разному, а завершённая процедура означает ' +
          'списанный долг. Должника этот файл не заводит - строка с незнакомым именем ' +
          'возвращается вам. Арбитр ЖКХ не узнаёт о новых банкротствах сам: доступ к ' +
          'реестру сведений о банкротстве подключён только в виде проверки на карточке ' +
          'счёта, и пока реестр у службы тестовый.'
        }
      />

      <section className="section card">
        <div className="field field--l">
          <label className="field__control">
            <span className="field__label">Файл с перечнем банкротов</span>
            <input
              key={`${round}-${flow.attempt}`}
              className="field__file"
              type="file"
              accept=".csv,.xlsx"
              disabled={busy || flow.busy}
              onChange={(event) => choose(event.target.files?.[0])}
            />
          </label>
        </div>
        <UploadStages flow={flow} />

        <div className="row">
          <button type="button" className="button" disabled={busy || !file || written} onClick={look}>
            Посмотреть, что получится
          </button>
          <button
            type="button"
            className="button"
            disabled={busy || !file || !plan || written}
            onClick={load}
          >
            Внести в портфель
          </button>
        </div>

        <Notice error={flow.error ?? error} />
        {/* Конец - блок «Готово» (20.10.2026); перечень процедур - под ним. */}
        {written ? (
          <UploadEnd
            title="Готово: процедуры банкротства внесены в портфель"
            facts={[
              ['Строк в файле', plan.total],
              ['Внесено процедур', plan.written],
              ['Строк, оставшихся вам', plan.issues.length],
            ]}
            seconds={flow.written?.seconds}
            onAgain={again}
          />
        ) : null}
        {plan ? <Plan plan={plan} /> : null}
      </section>
    </>
  )
}
